import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { Link } from 'react-router-dom'
import { useToast } from '../hooks/useToast'
import ModalConfirmacion from '../components/ModalConfirmacion'

export default function Clientes() {
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingCliente, setEditingCliente] = useState(null)
  const [modalConfirm, setModalConfirm] = useState({ isOpen: false, clienteId: null })

  // Estado del formulario
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [notas, setNotas] = useState('')

  const { addToast } = useToast()

  const fetchClientes = useCallback(async () => {
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) {
      addToast('Error al cargar clientes', 'error')
    } else {
      setClientes(data || [])
    }
    setLoading(false)
  }, [addToast])

  useEffect(() => {
    fetchClientes()
  }, [fetchClientes])

  const resetForm = () => {
    setNombre('')
    setEmail('')
    setTelefono('')
    setEmpresa('')
    setNotas('')
    setEditingCliente(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    if (editingCliente) {
      const { error } = await supabase
        .from('clientes')
        .update({ nombre, email, telefono, empresa, notas })
        .eq('id', editingCliente.id)

      if (error) {
        addToast('Error al actualizar cliente', 'error')
      } else {
        addToast('Cliente actualizado correctamente', 'success')
        setShowForm(false)
        resetForm()
        fetchClientes()
      }
    } else {
      const { error } = await supabase
        .from('clientes')
        .insert([{ nombre, email, telefono, empresa, notas, user_id: user.id }])

      if (error) {
        addToast('Error al crear cliente', 'error')
      } else {
        addToast('Cliente creado correctamente', 'success')
        setShowForm(false)
        resetForm()
        fetchClientes()
      }
    }
  }

  const handleEdit = (cliente) => {
    setEditingCliente(cliente)
    setNombre(cliente.nombre)
    setEmail(cliente.email || '')
    setTelefono(cliente.telefono || '')
    setEmpresa(cliente.empresa || '')
    setNotas(cliente.notas || '')
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    const { error } = await supabase.from('clientes').delete().eq('id', id)
    if (error) {
      addToast('Error al eliminar cliente', 'error')
    } else {
      addToast('Cliente eliminado correctamente', 'success')
      fetchClientes()
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando clientes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Modal de confirmación */}
      <ModalConfirmacion
        isOpen={modalConfirm.isOpen}
        onClose={() => setModalConfirm({ isOpen: false, clienteId: null })}
        onConfirm={() => handleDelete(modalConfirm.clienteId)}
        title="Eliminar cliente"
        message="¿Estás seguro de eliminar este cliente? También se eliminarán todos sus servicios asociados. Esta acción no se puede deshacer."
      />

      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Clientes</h1>
        <button
          onClick={() => {
            resetForm()
            setShowForm(!showForm)
          }}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {showForm ? 'Cancelar' : 'Nuevo Cliente'}
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Nombre *</label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Teléfono</label>
              <input
                type="text"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Empresa</label>
              <input
                type="text"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Notas</label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              {editingCliente ? 'Actualizar' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => {
                resetForm()
                setShowForm(false)
              }}
              className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de clientes */}
      {clientes.length === 0 ? (
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
          <p className="text-gray-500 text-lg">No hay clientes registrados</p>
          <p className="text-gray-400 mt-2">Haz clic en "Nuevo Cliente" para comenzar</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {clientes.map((cliente) => (
              <li key={cliente.id} className="hover:bg-gray-50 transition-colors">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Link
                      to={`/clientes/${cliente.id}`}
                      className="text-lg font-medium text-blue-600 hover:text-blue-800 truncate"
                    >
                      {cliente.nombre}
                    </Link>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleEdit(cliente)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setModalConfirm({ isOpen: true, clienteId: cliente.id })}
                        className="text-red-500 hover:text-red-600 text-sm font-medium"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <p className="text-gray-500 flex items-center gap-1">
                      <span className="text-gray-400">📧</span> {cliente.email || 'Sin email'}
                    </p>
                    <p className="text-gray-500 flex items-center gap-1">
                      <span className="text-gray-400">📞</span> {cliente.telefono || 'Sin teléfono'}
                    </p>
                    <p className="text-gray-500 flex items-center gap-1">
                      <span className="text-gray-400">🏢</span> {cliente.empresa || 'Sin empresa'}
                    </p>
                    <p className="text-gray-500 flex items-center gap-1">
                      <span className="text-gray-400">📝</span> {cliente.notas || 'Sin notas'}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}